import React from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Privacy() {
  const lastUpdated = "July 2026";
  return (
    <Layout>
      <div className="max-w-3xl mx-auto py-10" data-testid="privacy-page">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-brand-muted hover:text-brand-ink"
          data-testid="privacy-back-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} /> Back to home
        </Link>

        <h1 className="font-serif text-4xl sm:text-5xl text-brand-ink mt-6 leading-tight">
          Privacy Notice
        </h1>
        <p className="text-sm text-brand-muted mt-2">Last updated: {lastUpdated}</p>

        <div className="prose prose-lg max-w-none mt-10 text-brand-body leading-relaxed text-pretty" style={{ textWrap: "pretty" }}>
          <p>
            This Privacy Notice explains how <strong>Calside — Practice training for UK general practice</strong>{" "}
            ("Calside", "we", "us") collects, uses and looks after personal data when you use the
            Calside training platform. We take your privacy seriously and comply with the{" "}
            <strong>UK General Data Protection Regulation (UK GDPR)</strong> and the{" "}
            <strong>Data Protection Act 2018</strong>.
          </p>

          <h2 className="font-serif text-2xl text-brand-ink mt-10">1. Who we are</h2>
          <p>
            Calside is a training platform created by Grant Cameron Anthony for GP practice teams
            across the UK. For&nbsp;the purposes of UK&nbsp;GDPR, the <strong>data controller</strong> is:
          </p>
          <ul>
            <li>Grant Cameron Anthony, Calside — Practice training for UK general practice</li>
            <li>
              Contact: <a href="mailto:Grant.Anthony2@nhs.scot">Grant.Anthony2@nhs.scot</a>
            </li>
            <li>
              WhatsApp / phone: <a href="tel:+447590335390">07590 335390</a>
            </li>
          </ul>
          <p>
            When your practice invites you to Calside using a practice invite code, your Practice
            Manager acts as the primary data controller for progress and completion records related
            to your employment; Calside acts as processor for that content and as controller for the
            platform account itself.
          </p>

          <h2 className="font-serif text-2xl text-brand-ink mt-10">2. What personal data we collect</h2>
          <ul>
            <li>
              <strong>Account data:</strong> your name, work email address, hashed password, role
              (learner / Practice Manager / super admin), and the practice you belong to.
            </li>
            <li>
              <strong>Learning data:</strong> which courses and lessons you have opened, quiz
              scores, flashcard spaced-repetition state, completion timestamps, and downloaded
              certificates.
            </li>
            <li>
              <strong>Technical data:</strong> IP address at login, browser type, and session
              cookies necessary for authentication.
            </li>
          </ul>
          <p>
            <strong>Special category data:</strong> we do not routinely collect health, ethnicity,
            religion or other special-category data. You should never enter patient-identifiable
            information into any Calside field.
          </p>
          <p>
            <strong>No paid tier, no card data on Calside.</strong> Calside itself is 100% free —
            there is no subscription and no paid tier. Optional donations are handled entirely by{" "}
            <strong>Stripe</strong> on Stripe-hosted pages; card details are entered on Stripe's
            own checkout and are never seen, transmitted through, or stored by Calside.
          </p>

          <h2 className="font-serif text-2xl text-brand-ink mt-10">3. Lawful bases (UK GDPR Article 6)</h2>
          <ul>
            <li>
              <strong>Contract (Art. 6(1)(b)):</strong> to provide the training platform you or your
              practice have signed up for.
            </li>
            <li>
              <strong>Legitimate interests (Art. 6(1)(f)):</strong> to keep the platform secure,
              improve the learning experience, and communicate essential service updates.
            </li>
            <li>
              <strong>Legal obligation (Art. 6(1)(c)):</strong> to keep financial records where
              payments are processed, and to respond to lawful requests from regulators or the
              courts.
            </li>
            <li>
              <strong>Consent (Art. 6(1)(a)):</strong> where we ever ask for optional analytics or
              marketing consent, we will do so explicitly and you can withdraw at any time.
            </li>
          </ul>

          <h2 className="font-serif text-2xl text-brand-ink mt-10">4. Who we share your data with</h2>
          <p>We use a small number of vetted processors, each bound by a data-processing agreement:</p>
          <ul>
            <li>
              <strong>MongoDB Atlas / hosting provider</strong> — stores the account and learning
              database in encrypted form. UK / EEA region unless notified.
            </li>
            <li>
              <strong>Your Practice Manager</strong> — sees your progress and completion status as
              part of practice-level oversight of mandatory training.
            </li>
            <li>
              <strong>Stripe Payments Europe, Ltd.</strong> — only if you choose to make an
              optional donation via the footer "Keep it afloat" links. You are redirected to
              Stripe's own hosted checkout; Stripe processes the payment and holds the card
              data, not Calside. See{" "}
              <a
                href="https://stripe.com/gb/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-brand-accent"
              >
                Stripe's privacy policy
              </a>
              .
            </li>
          </ul>
          <p>
            We do not sell your personal data to anyone, and we do not use it for advertising.
            We use no AI processors and no advertising networks. The only payment processor we
            interact with is Stripe, and only when you voluntarily make a donation.
          </p>

          <h2 className="font-serif text-2xl text-brand-ink mt-10">5. International transfers</h2>
          <p>
            All personal data is stored in the UK / EEA. Where any sub-processor operates outside
            the UK / EEA, we rely on the UK International Data Transfer Agreement (IDTA) or
            Standard Contractual Clauses (SCCs) in accordance with UK GDPR. All transfers are
            encrypted in transit.
          </p>

          <h2 className="font-serif text-2xl text-brand-ink mt-10">6. How long we keep your data</h2>
          <ul>
            <li>
              <strong>Account and learning records:</strong> retained while your practice remains
              on Calside, and for a further 12 months after you leave your practice, so that
              certificates and progress records remain retrievable for revalidation and appraisal.
            </li>
            <li>
              <strong>Backups:</strong> encrypted backups are kept on a rolling 30-day cycle and
              are then overwritten.
            </li>
          </ul>

          <h2 className="font-serif text-2xl text-brand-ink mt-10">7. Your rights under UK GDPR</h2>
          <p>You have the following rights, free of charge:</p>
          <ul>
            <li>
              <strong>Right of access</strong> — request a copy of the personal data we hold about
              you.
            </li>
            <li>
              <strong>Right to rectification</strong> — ask us to correct inaccurate data.
            </li>
            <li>
              <strong>Right to erasure</strong> ("right to be forgotten") — ask us to delete your
              data, subject to legal retention obligations.
            </li>
            <li>
              <strong>Right to restrict processing</strong> — pause our use of your data while a
              dispute is resolved.
            </li>
            <li>
              <strong>Right to data portability</strong> — receive your account and progress data
              in a portable format.
            </li>
            <li>
              <strong>Right to object</strong> — object to processing based on legitimate
              interests.
            </li>
            <li>
              <strong>Rights in relation to automated decision-making</strong> — we do not use
              automated decisions with legal effect. Calside is a learning platform, not a
              decision-maker.
            </li>
          </ul>
          <p>
            To exercise any right, email{" "}
            <a href="mailto:Grant.Anthony2@nhs.scot">Grant.Anthony2@nhs.scot</a>. We will respond within
            one month.
          </p>

          <h2 className="font-serif text-2xl text-brand-ink mt-10">8. Security</h2>
          <p>
            Passwords are hashed using bcrypt. All traffic is served over HTTPS/TLS. Access to
            production databases is restricted to named administrators and logged. We follow
            industry-standard practices around role-based access control, secret management and
            vulnerability patching. In the unlikely event of a personal-data breach that meets the
            reporting threshold, we will notify the Information Commissioner's Office (ICO) within
            72 hours and affected users without undue delay, in accordance with UK GDPR Articles
            33-34.
          </p>

          <h2 className="font-serif text-2xl text-brand-ink mt-10">9. Cookies &amp; analytics</h2>
          <p>
            Calside uses <strong>essential cookies</strong> to sign you in and keep you signed in.
            These cannot be disabled — without them the platform cannot function.
          </p>
          <p className="mt-3">
            With your consent, Calside also uses <strong>Google Analytics 4</strong> (measurement ID
            G-JTR1DTFZ9K) to understand which pages are visited and improve the platform. Analytics
            starts in a <strong>denied</strong> state under{" "}
            <a
              href="https://developers.google.com/tag-platform/security/guides/consent"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2"
            >Google Consent Mode v2</a>{" "}
            — no analytics cookies are set and no measurement is sent to Google until you accept
            the banner. IP addresses are anonymised. We do not use advertising cookies,
            ad-personalisation cookies, or third-party tracking pixels.
          </p>
          <p className="mt-3">
            You can change your decision at any time by clearing your browser&apos;s localStorage
            for this site — the banner will reappear on next visit and you can choose again.
          </p>

          <h2 className="font-serif text-2xl text-brand-ink mt-10">10. Children</h2>
          <p>
            Calside is a professional training platform for adult healthcare staff. It is not
            directed at children under 18. If you believe a child has registered an account, please
            contact us at once so we can remove it.
          </p>

          <h2 className="font-serif text-2xl text-brand-ink mt-10">11. Complaints</h2>
          <p>
            If you have a concern about how we handle your personal data, please contact us first
            at <a href="mailto:Grant.Anthony2@nhs.scot">Grant.Anthony2@nhs.scot</a>. If you remain
            unsatisfied, you have the right to complain to the{" "}
            <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noreferrer">
              Information Commissioner's Office (ICO)
            </a>
            :
          </p>
          <ul>
            <li>Website: ico.org.uk</li>
            <li>Telephone: 0303 123 1113</li>
            <li>Post: Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF</li>
          </ul>

          <h2 className="font-serif text-2xl text-brand-ink mt-10">13. Changes to this notice</h2>
          <p>
            We may update this Privacy Notice from time to time. The "Last updated" date at the
            top of this page reflects the most recent change. Material changes will be notified to
            you by email or an in-app notice.
          </p>

          <p className="mt-10 text-sm text-brand-muted italic">
            This notice is written for the whole practice team — reception, admin, HCAs, nurses,
            ANPs, GPs and Practice Managers. If any section is unclear, tell us — a policy that
            isn't understood does no one any good.
          </p>
        </div>
      </div>
    </Layout>
  );
}
